import type { VehicleSpec } from './types';

/**
 * Datengetriebene Fahrzeugdefinitionen. Ein neues Fahrzeug hinzuzufuegen
 * heisst: Modell ablegen + hier einen Eintrag ergaenzen.
 *
 * Konvention: +Z ist vorne, +Y ist oben, +X ist links.
 * (Rapiers Raycast-Vehicle kennt nur Achsen-Indizes ohne Vorzeichen,
 * deshalb ist "vorne" hier +Z und nicht wie in Three.js -Z.)
 */

const STADTFLITZER: VehicleSpec = {
  id: 'stadtflitzer',
  name: 'STADTFLITZER',
  category: 'car',
  mass: 950,
  chassisHalfExtents: { x: 0.78, y: 0.36, z: 1.75 },
  chassisOffsetY: 0.66,
  centerOfMassY: 0.34,
  inertiaFactor: 0.78,
  wheels: {
    radius: 0.32,
    width: 0.22,
    halfTrack: 0.74,
    frontZ: 1.22,
    rearZ: -1.24,
    connectionY: 0.42,
  },
  drive: 'fwd',
  engineForce: 1750,
  topSpeedKmh: 95,
  reverseSpeedKmh: 28,
  brakeForce: 34,
  handbrakeForce: 130,
  engineBrakeFactor: 0.07,
  maxSteerDeg: 36,
  steerRate: 4.2,
  steerReturnRate: 7.5,
  highSpeedSteerFactor: 0.42,
  // Federsteifigkeit so gewaehlt, dass im Stand rund ein Drittel des
  // Federwegs genutzt wird. Ausgemessen mit `npm run tune`: der statische
  // Einfederweg ist massenunabhaengig und betraegt g / (4 * stiffness),
  // deshalb braucht das Coupé mit kurzem Federweg die hoechste Steifigkeit.
  suspension: {
    restLength: 0.34,
    stiffness: 55,
    compression: 0.9,
    relaxation: 1.15,
    maxTravel: 0.22,
    maxForce: 12000,
  },
  frictionSlip: 2.1,
  sideFrictionStiffness: 0.9,
  handbrakeGripFactor: 0.32,
  downforceAtTopSpeed: 0.25,
  stats: { speed: 0.45, power: 0.35, offroad: 0.3 },
  proxy: {
    bodyColor: 0xe23b2e,
    lower: { w: 1.62, h: 0.5, l: 3.6, y: 0.62 },
    cabin: { w: 1.42, h: 0.5, l: 1.8, y: 1.1, z: -0.12 },
  },
};

const FAMILIEN_SUV: VehicleSpec = {
  id: 'familien-suv',
  name: 'FAMILIEN-SUV',
  category: 'car',
  mass: 1650,
  chassisHalfExtents: { x: 0.92, y: 0.46, z: 2.15 },
  chassisOffsetY: 0.86,
  centerOfMassY: 0.46,
  inertiaFactor: 0.95,
  wheels: {
    radius: 0.4,
    width: 0.28,
    halfTrack: 0.86,
    frontZ: 1.5,
    rearZ: -1.48,
    connectionY: 0.58,
  },
  drive: 'awd',
  engineForce: 2100,
  topSpeedKmh: 110,
  reverseSpeedKmh: 30,
  brakeForce: 59,
  handbrakeForce: 210,
  engineBrakeFactor: 0.08,
  maxSteerDeg: 33,
  steerRate: 3.4,
  steerReturnRate: 6.2,
  highSpeedSteerFactor: 0.45,
  suspension: {
    restLength: 0.44,
    stiffness: 40,
    compression: 1.0,
    relaxation: 1.3,
    maxTravel: 0.3,
    maxForce: 20000,
  },
  frictionSlip: 2.3,
  sideFrictionStiffness: 0.85,
  handbrakeGripFactor: 0.4,
  downforceAtTopSpeed: 0.3,
  stats: { speed: 0.55, power: 0.6, offroad: 0.75 },
  proxy: {
    bodyColor: 0x2f6fd0,
    lower: { w: 1.9, h: 0.66, l: 4.45, y: 0.82 },
    cabin: { w: 1.72, h: 0.66, l: 2.5, y: 1.48, z: -0.16 },
  },
};

const SPORTCOUPE: VehicleSpec = {
  id: 'sportcoupe',
  name: 'SPORTCOUPÉ',
  category: 'car',
  mass: 1180,
  chassisHalfExtents: { x: 0.9, y: 0.3, z: 2.05 },
  chassisOffsetY: 0.56,
  centerOfMassY: 0.24,
  inertiaFactor: 0.7,
  wheels: {
    radius: 0.34,
    width: 0.3,
    halfTrack: 0.86,
    frontZ: 1.42,
    rearZ: -1.4,
    connectionY: 0.36,
  },
  drive: 'rwd',
  engineForce: 3400,
  topSpeedKmh: 160,
  reverseSpeedKmh: 30,
  brakeForce: 53,
  handbrakeForce: 190,
  engineBrakeFactor: 0.06,
  maxSteerDeg: 32,
  steerRate: 4.8,
  steerReturnRate: 8.5,
  highSpeedSteerFactor: 0.3,
  suspension: {
    restLength: 0.3,
    stiffness: 80,
    compression: 1.1,
    relaxation: 1.4,
    maxTravel: 0.16,
    maxForce: 16000,
  },
  frictionSlip: 2.4,
  sideFrictionStiffness: 1.0,
  handbrakeGripFactor: 0.22,
  downforceAtTopSpeed: 0.55,
  stats: { speed: 0.95, power: 0.9, offroad: 0.15 },
  proxy: {
    bodyColor: 0xf5c518,
    lower: { w: 1.86, h: 0.44, l: 4.25, y: 0.52 },
    cabin: { w: 1.5, h: 0.4, l: 1.9, y: 0.94, z: -0.34 },
  },
};

const KOMPAKTBAGGER: VehicleSpec = {
  ...FAMILIEN_SUV,
  id: 'kompaktbagger',
  name: 'KOMPAKTBAGGER',
  category: 'construction',
  mass: 2300,
  engineForce: 2450,
  topSpeedKmh: 42,
  reverseSpeedKmh: 20,
  brakeForce: 78,
  maxSteerDeg: 42,
  highSpeedSteerFactor: 0.62,
  stats: { speed: 0.18, power: 0.82, offroad: 0.88 },
  proxy: {
    kind: 'excavator',
    bodyColor: 0xf5b51b,
    lower: { w: 1.88, h: 0.62, l: 3.9, y: 0.76 },
    cabin: { w: 1.35, h: 0.9, l: 1.6, y: 1.52, z: -0.55 },
  },
};

const MULDENKIPPER: VehicleSpec = {
  ...FAMILIEN_SUV,
  id: 'muldenkipper',
  name: 'MULDENKIPPER',
  category: 'construction',
  mass: 4200,
  chassisHalfExtents: { x: 1.08, y: 0.58, z: 2.45 },
  chassisOffsetY: 1.02,
  centerOfMassY: 0.38,
  wheels: {
    radius: 0.52,
    width: 0.36,
    halfTrack: 0.98,
    frontZ: 1.7,
    rearZ: -1.62,
    connectionY: 0.65,
  },
  engineForce: 3900,
  topSpeedKmh: 58,
  reverseSpeedKmh: 22,
  brakeForce: 125,
  handbrakeForce: 360,
  maxSteerDeg: 31,
  steerRate: 2.8,
  stats: { speed: 0.24, power: 1, offroad: 0.92 },
  proxy: {
    kind: 'dumper',
    bodyColor: 0xf2a900,
    lower: { w: 2.18, h: 0.72, l: 5.0, y: 0.94 },
    cabin: { w: 1.85, h: 1.0, l: 1.45, y: 1.78, z: 1.36 },
  },
};

export const VEHICLES: readonly VehicleSpec[] = [
  STADTFLITZER,
  FAMILIEN_SUV,
  SPORTCOUPE,
  KOMPAKTBAGGER,
  MULDENKIPPER,
];

export const VEHICLES_BY_ID: ReadonlyMap<string, VehicleSpec> = new Map(
  VEHICLES.map((v) => [v.id, v]),
);

export const DEFAULT_VEHICLE_ID = STADTFLITZER.id;

export function getVehicleSpec(id: string): VehicleSpec {
  const spec = VEHICLES_BY_ID.get(id);
  if (!spec) throw new Error(`Unbekanntes Fahrzeug: ${id}`);
  return spec;
}
